import { Ball } from './destiny/ball.js';
import { BallMode } from './destiny/constants.js';

/**
 * Wire protocol. Two replication packet kinds, exactly like Destiny:
 * FULLSTATE on bubble entry, deltas (stamped events) after. Clients run the
 * same integrator and extrapolate between server stamps.
 */

/** Everything a client needs to deterministically extrapolate a ball. */
export interface BallSnapshot {
  id: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  mode: BallMode;
  radius: number;
  mass: number;
  maxVel: number;
  agility: number;
  speedFraction: number;
  gx: number; // goto
  gy: number;
  gz: number;
  followId: number;
  followRange: number;
  ownerId: number;
  effectStamp: number;
  lastCollision: number; // warp length when warping
  warpMinRange: number;
  formationId: number;
  isMassive: boolean;
  isGlobal: boolean;
  isFree: boolean;
  yaw: number;
  pitch: number;
  roll: number;
  // Game-layer decoration (not part of the sim)
  kind: string; // 'ship' | 'station' | 'gate' | 'sun' | 'belt' | 'wreck' | 'npc' | ...
  name?: string;
  shipClass?: string;
  faction?: string;
  charId?: number;
  toSystem?: number; // gates only
}

export function ballToSnapshot(ball: Ball, deco: Partial<BallSnapshot> = {}): BallSnapshot {
  return {
    id: ball.id,
    x: ball.newPos.x,
    y: ball.newPos.y,
    z: ball.newPos.z,
    vx: ball.newVel.x,
    vy: ball.newVel.y,
    vz: ball.newVel.z,
    mode: ball.mode,
    radius: ball.radius,
    mass: ball.mass,
    maxVel: ball.maxVel,
    agility: ball.agility,
    speedFraction: ball.speedFraction,
    gx: ball.goto.x,
    gy: ball.goto.y,
    gz: ball.goto.z,
    followId: ball.followId,
    followRange: ball.followRange,
    ownerId: ball.ownerId,
    effectStamp: ball.effectStamp,
    lastCollision: ball.lastCollision,
    warpMinRange: ball.warpMinRange,
    formationId: ball.formationId,
    isMassive: ball.isMassive,
    isGlobal: ball.isGlobal,
    isFree: ball.isFree,
    yaw: ball.newYaw,
    pitch: ball.newPitch,
    roll: ball.newRoll,
    kind: 'ship',
    ...deco,
  };
}

// ---------------------------------------------------------------------
// Client → server
// ---------------------------------------------------------------------

export type ClientMessage =
  | { t: 'hello'; handle: string; token: string }
  | { t: 'goto'; x: number; y: number; z: number }
  | { t: 'approach'; targetId: number }
  | { t: 'orbit'; targetId: number; range: number }
  | { t: 'keepRange'; targetId: number; range: number }
  | { t: 'warp'; x: number; y: number; z: number; minRange?: number }
  | { t: 'warpTo'; targetId: number; minRange?: number }
  | { t: 'stop' }
  | { t: 'dock'; stationId: number }
  | { t: 'undock' }
  | { t: 'jump'; gateId: number }
  | { t: 'say'; text: string }
  | { t: 'ping'; n: number }
  // Combat & targeting
  | { t: 'lock'; targetId: number }
  | { t: 'unlock' }
  | { t: 'activate'; slot: number }
  | { t: 'deactivate'; slot: number }
  // Mining
  | { t: 'tune'; value: number } // resonance slider 0..1
  // Station services (docked)
  | { t: 'fit'; modules: string[] }
  | { t: 'refine'; ore: string; qty: number }
  | { t: 'build'; blueprint: string } // ship class or module key
  | { t: 'switchShip'; classKey: string }
  | { t: 'order'; side: 'buy' | 'sell'; item: string; qty: number; price: number }
  | { t: 'cancelOrder'; orderId: number }
  | { t: 'stationInfo' }
  | { t: 'missions'; action: 'list' | 'accept' | 'complete' | 'abandon'; id?: string }
  // Wrecks
  | { t: 'loot'; wreckId: number }
  // Docked cargo <-> hangar transfer
  | { t: 'transfer'; item: string; qty: number; dir: 'toCargo' | 'toHangar' };

// ---------------------------------------------------------------------
// Server → client
// ---------------------------------------------------------------------

export interface SystemInfo {
  id: number;
  name: string;
  region: string;
  security: 'core' | 'verge' | 'deep';
}

export type ReplicationEvent =
  | { e: 'enter'; ball: BallSnapshot } // ball entered your bubble
  | { e: 'exit'; ballId: number } // ball left your bubble / removed
  | { e: 'mode'; ball: BallSnapshot } // mode/state change: new initial condition
  | { e: 'warpStart'; ballId: number }
  | { e: 'warpEnd'; ballId: number };

export type ServerMessage =
  | { t: 'welcome'; charId: number; name: string; system: SystemInfo; docked: number | null; shipBallId: number | null }
  | { t: 'error'; message: string }
  | { t: 'fullstate'; tick: number; stamp: number; system: SystemInfo; balls: BallSnapshot[]; yourBallId: number | null }
  | { t: 'delta'; tick: number; stamp: number; events: ReplicationEvent[] }
  | { t: 'docked'; stationId: number }
  | { t: 'undocked'; shipBallId: number }
  | { t: 'jumped'; system: SystemInfo }
  | { t: 'chat'; from: string; text: string; channel: string }
  | { t: 'pong'; n: number; tick: number }
  // Per-tick own-ship combat/mining status
  | {
      t: 'status';
      shield: number; armor: number; hull: number;
      shieldMax: number; armorMax: number; hullMax: number;
      cap: number; capMax: number;
      locked: number | null; lockProgress: number;
      scrambled: boolean;
      modules: { key: string; slot: string; active: boolean; cycle: number; heat: number; disabled: boolean }[];
      resonance: { tune: number; target: number; band: number } | null;
      cargoUsed: number; cargoMax: number;
      marks: number;
      mastery: Record<string, number>;
      hitQuality: number | null; // live transversal quality vs locked target 0..1
    }
  | { t: 'combat'; kind: 'hit' | 'miss'; from: number; to: number; dmg: number; quality: number }
  | { t: 'destroyed'; ballId: number; wreckId: number | null; byName?: string }
  | { t: 'youDied'; respawnStation: number; lost: string }
  | { t: 'minedOre'; ore: string; qty: number; rockId: number; remaining: number }
  | {
      t: 'stationInfo';
      stationId: number; name: string; faction: string;
      hangar: { items: Record<string, number>; ships: string[] };
      cargo: Record<string, number>;
      fitted: string[];
      shipClass: string;
      marks: number;
      orders: { id: number; side: string; item: string; qty: number; price: number; own: boolean }[];
      blueprints: { key: string; kind: 'ship' | 'module'; cost: Record<string, number>; fee: number; locked: boolean }[];
      missions: { id: string; kind: string; title: string; desc: string; reward: number; accepted: boolean; complete: boolean }[];
      mastery: Record<string, number>;
    }
  | { t: 'notice'; text: string }
  // Transient visual effects, broadcast to everyone who can see them
  | {
      t: 'fx';
      kind: 'tracer' | 'miss' | 'beam' | 'explosion' | 'shieldhit' | 'warpin' | 'warpout';
      from?: number;
      to?: number;
      x?: number;
      y?: number;
      z?: number;
      size?: number;
    }
  // Story chain state (The Bell Below)
  | { t: 'story'; chapter: number; title: string; body: string; objective: string; done: boolean };

export const encode = (m: ClientMessage | ServerMessage): string => JSON.stringify(m);
export const decode = <T>(raw: string): T | null => {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};
